import { api_configuration } from "common";
import api_main = api_configuration.libraries.gui_definition_lib.types.main.alan_api;
import api_client_state = api_configuration.types.client_state.alan_api;
import api_settings = api_configuration.libraries.gui_definition_lib.types.settings.alan_api;
import api_application = api_configuration.libraries.gui_definition_lib.libraries.model_lib.types.application.alan_api;
import { client_state, gui_model } from "../state/ClientState";
import { IdPathBuilder } from "../navigation/IdPathBuilder";
import { DatastoreProxy } from "../interface/DatastoreProxy";
import { CollectionPathBuilder } from "../navigation/createCollectionPathBuilder";
import openViewOnNode from "./openViewOnNode";
import * as mapToIdPathBuilder from "../state/mapToIdPathBuilder";
import * as Rule from "../state/Rule";
import NodePathBuilder from "../navigation/NodePathBuilder";
import { Failed } from "../Status";

export default function openLandingPage(main: api_main.Cmain): void {
	let has_landing_page = false;
	for (const [, initial_view_expression] of main.properties.initial_content) {
		switch (initial_view_expression.properties.from.state.name) {
			case "root": {
				visitViewExpression(
					initial_view_expression.properties.expression,
					IdPathBuilder.create(),
					initial_view_expression.key.ref
				);
				has_landing_page = true;
				break;
			}
			case "user": {
				switch (client_state.properties.session.properties.status.state.name) {
					case "authenticated": {
						const authenticated = client_state.properties.session.properties.status.state.node;
						switch (authenticated.properties.anonymous_session.state.name) {
							case "no": {
								const not_anonymous = authenticated.properties.anonymous_session.state.node;
								const user_id = createIdFromUser(not_anonymous);
								has_landing_page = true;
								visitViewExpression(
									initial_view_expression.properties.expression,
									user_id,
									initial_view_expression.key.ref
								);
								break;
							}
							case "yes":
								break;
							default:
								unexpectedState(authenticated.properties.anonymous_session.state);
						}
						break;
					}
					case "not authenticated":
						break;
					default:
						unexpectedState(client_state.properties.session.properties.status.state);
				}
				break;
			}
			default:
				unexpectedState(initial_view_expression.properties.from.state);
		}
	}
	if (has_landing_page) {
		client_state.properties.has_landing_page.ensure("yes", {});
	} else {
		client_state.properties.has_landing_page.ensure("no", {});
	}
}

async function visitViewExpression(
	expression: api_main.Cview_expression,
	context: IdPathBuilder,
	target_window: api_settings.Cwindows
): Promise<boolean> {
	switch (expression.properties.type.state.name) {
		case "branches": {
			const $ = expression.properties.type.state.node;
			return await visitViewExpressionList($.properties.list, context, target_window);
		}
		case "collection lookup": {
			const $ = expression.properties.type.state.node;
			switch (client_state.properties.session.properties.status.state.name) {
				case "authenticated": {
					const authenticated = client_state.properties.session.properties.status.state.node;
					switch (authenticated.properties.anonymous_session.state.name) {
						case "no": {
							const not_anonymous = authenticated.properties.anonymous_session.state.node;
							return await visitViewExpression(
								$.properties.expression,
								context.collectionEntry($.properties.collection.entry, not_anonymous.properties.username),
								target_window
							);
						}
						case "yes":
							return false;
						default:
							return unexpectedState(authenticated.properties.anonymous_session.state);
					}
				}
				case "not authenticated":
					return false;
				default:
					return unexpectedState(client_state.properties.session.properties.status.state);
			}
		}
		case "group step": {
			const $ = expression.properties.type.state.node;
			return await visitViewExpression($.properties.expression, context.group($.properties.group.entry), target_window);
		}
		case "none": {
			return false;
		}
		case "reference step": {
			const $ = expression.properties.type.state.node;
			let reference: IdPathBuilder | undefined;
			switch ($.properties.type.state.name) {
				case "reference": {
					const $reference = $.properties.type.state.node;
					reference = await referencedEntry(context, $reference.properties.reference.entry);
					break;
				}
				case "property rule": {
					const $property_rule = $.properties.type.state.node;
					reference = await new Promise((resolve) => {
						const stream = Rule.query_constraint(
							context,
							new NodePathBuilder().reference_rule(
								$property_rule.properties.property.entry,
								$property_rule.properties.rule.entry
							)
						);
						stream.subscribe({
							update: (result) => {
								if (result === Failed) {
									stream.destroy();
									resolve(undefined);
								}
								if (result instanceof IdPathBuilder) {
									stream.destroy();
									resolve(result);
								}
							},
						});
					});
					break;
				}
				case "context rule": {
					const $context_rule = $.properties.type.state.node;
					reference = await new Promise((resolve) => {
						const stream = Rule.query_constraint(
							context,
							new NodePathBuilder().state_rule($context_rule.properties.rule.entry)
						);
						stream.subscribe({
							update: (result) => {
								if (result === Failed) {
									stream.destroy();
									resolve(undefined);
								}
								if (result instanceof IdPathBuilder) {
									stream.destroy();
									resolve(result);
								}
							},
						});
					});
					break;
				}
			}
			if (reference === undefined) {
				return false;
			} else {
				return await visitViewExpression($.properties.expression, reference, target_window);
			}
			break;
		}
		case "state switch": {
			const $ = expression.properties.type.state.node;
			for (const [state_name, case_] of $.properties.cases) {
				const state_context = context.state($.properties.stategroup.entry, state_name);
				if (
					(await contextExists(state_context)) &&
					(await visitViewExpression(
						case_.properties.expression,
						visitAncestor(state_context, case_.properties.path),
						target_window
					))
				) {
					return true;
				}
			}
			return false;
		}
		case "view open": {
			const $ = expression.properties.type.state.node;
			if (await contextExists(context)) {
				return openView(context, $, target_window);
			} else {
				return false;
			}
		}
		default:
			unexpectedState(expression.properties.type.state);
	}
}

async function visitViewExpressionList(
	expression: api_main.Cview_expression_list,
	context: IdPathBuilder,
	target_window: api_settings.Cwindows
): Promise<boolean> {
	if (await visitViewExpression(expression.properties.expression, context, target_window)) {
		return true;
	} else {
		switch (expression.properties.has_alternative.state.name) {
			case "no": {
				return false;
			}
			case "yes": {
				const $ = expression.properties.has_alternative.state.node;
				return await visitViewExpressionList($.properties.tail, context, target_window);
			}
			default:
				unexpectedState(expression.properties.has_alternative.state);
		}
	}
}

function openView(
	context: IdPathBuilder,
	view_open: api_main.Cview_open,
	target_window: api_settings.Cwindows
): boolean {
	switch (view_open.properties.node.state.name) {
		case "current": {
			const $ = view_open.properties.node.state.node;
			openViewOnNode(visitAncestor(context, $.properties.path), view_open.properties.view.ref, target_window, false);
			return true;
		}
		case "root": {
			openViewOnNode(IdPathBuilder.create(), view_open.properties.view.ref, target_window, false);
			return true;
		}
		case "user": {
			switch (client_state.properties.session.properties.status.state.name) {
				case "authenticated": {
					const authenticated = client_state.properties.session.properties.status.state.node;
					switch (authenticated.properties.anonymous_session.state.name) {
						case "no": {
							const not_anonymous = authenticated.properties.anonymous_session.state.node;
							const user_id = createIdFromUser(not_anonymous);
							openViewOnNode(user_id, view_open.properties.view.ref, target_window, false);
							return true;
						}
						case "yes": {
							return true;
						}
						default:
							return unexpectedState(authenticated.properties.anonymous_session.state);
					}
				}
				case "not authenticated": {
					return false;
				}
				default:
					return unexpectedState(client_state.properties.session.properties.status.state);
			}
		}
		default:
			unexpectedState(view_open.properties.node.state);
	}
}

function createIdFromUser(not_anonymous: api_client_state.Cno__anonymous_session): IdPathBuilder {
	const users_collection_path =
		gui_model.input.application.properties.has_dynamic_users.cast("yes").properties.users_collection_path;
	const users_collection = users_collection_path.properties.property.entry;
	const users_collection_context = vititObjectPathTail(IdPathBuilder.create(), users_collection_path.properties.head);
	return users_collection_context.collectionEntry(users_collection, not_anonymous.properties.username);
}

async function contextExists(context: IdPathBuilder): Promise<boolean> {
	const result = await DatastoreProxy.instance.collectionQuery({
		"context node path": context.build(),
		"collection path": CollectionPathBuilder.create().build(),
		"select entries": "no",
		filters: {
			"todo filter": ["no", {}],
			"value filters": {},
		},
		aggregates: {},
	});

	switch (result.properties.context.state.name) {
		case "does not exist": {
			return false;
		}
		case "exists": {
			return true;
		}
		case "permission denied": {
			return false;
		}
		default:
			unexpectedState(result.properties.context.state);
	}
}

async function referencedEntry(context: IdPathBuilder, reference: string): Promise<IdPathBuilder | undefined> {
	const result = await DatastoreProxy.instance.subscribeOnce({
		"context node": context.build(),
		"mutation permissions": "ignore",
		"node subscription": {
			attributes: {
				[reference]: {
					type: [
						"property",
						{
							type: [
								"text",
								{
									"include reference": ["yes", {}],
								},
							],
						},
					],
				},
			},
		},
	});
	switch (result.properties.context.state.name) {
		case "does not exist": {
			return undefined;
		}
		case "exists": {
			const $ = result.properties.context.state.node;
			switch ($.properties.subscription_result_data.properties.read_permitted.state.name) {
				case "no": {
					return undefined;
				}
				case "yes": {
					const read_permitted = $.properties.subscription_result_data.properties.read_permitted.state.node;
					const reference_result = read_permitted.properties.attributes.get(reference);
					if (reference_result === undefined) {
						return undefined;
					} else {
						const text_property = reference_result.properties.type.cast("property").properties.type.cast("text");
						switch (text_property.properties.reference_result.state.name) {
							case "no": {
								return undefined;
							}
							case "yes": {
								const $ = text_property.properties.reference_result.state.node;
								switch ($.properties.result.state.name) {
									case "disconnected": {
										return undefined;
									}
									case "node": {
										const node = $.properties.result.state.node;
										return mapToIdPathBuilder.fromClientReply(node.properties.id_path);
									}
									case "permission denied": {
										return undefined;
									}
									default:
										return unexpectedState($.properties.result.state);
								}
							}
							default:
								return unexpectedState(text_property.properties.reference_result.state);
						}
					}
				}
				default:
					return unexpectedState($.properties.subscription_result_data.properties.read_permitted.state);
			}
		}
		case "permission denied": {
			return undefined;
		}
		default:
			unexpectedState(result.properties.context.state);
	}
}

function visitAncestor(context: IdPathBuilder, path: api_main.Cancestor_node_path): IdPathBuilder {
	return path.properties.has_step.switch({
		no: context,
		yes: ($) => visitAncestor(context.removeTail(), $.properties.tail),
	});
}

function vititObjectPathTail(id: IdPathBuilder, path: api_application.Cobject_path_tail): IdPathBuilder {
	let property: string | undefined;
	while (path.properties.has_steps.state.name === "yes") {
		const has_steps = path.properties.has_steps.state.node;
		switch (has_steps.properties.type.state.name) {
			case "property value": {
				const $ = has_steps.properties.type.state.node;
				property = $.properties.property.properties.property.entry;
				switch ($.properties.property.properties.property.ref.properties.type.state.name) {
					case "group": {
						id = id.group(property);
						property = undefined;
						break;
					}
					case "state group":
						break;
					case "text":
						throw new Error("unexpected step for conditional path");
					case "number":
						throw new Error("unexpected step for conditional path");
					case "file":
						throw new Error("unexpected step for conditional path");
					case "collection":
						throw new Error("unexpected step for conditional path");
					default:
						unexpectedState($.properties.property.properties.property.ref.properties.type.state);
				}
				break;
			}
			case "state":
				if (property !== undefined) {
					const state = has_steps.properties.type.state.node.properties.state.entry;
					id = id.state(property, state);
					property = undefined;
				} else {
					throw new Error("unexpected step for conditional path");
				}
				break;
			case "parent":
				throw new Error("unexpected step for conditional path");
			case "dependency":
				throw new Error("unexpected step for conditional path");
			case "reference set":
				throw new Error("unexpected step for conditional path");
			default:
				unexpectedState(has_steps.properties.type.state);
		}
		path = has_steps.properties.tail;
	}
	return id;
}
